import { useCallback, useEffect, useState } from 'react';
import { ERROR_408, ERROR_409, ERROR_504 } from 'common/config/api';
import { toString, typedObjectValues } from 'utilities';
import {
  COMPLETE,
  CONFLICT,
  FAILED,
  INITIAL,
  LongGatewayRequestParams,
  MultiPromiseMap,
  PENDING,
  POLLING,
  PromiseResult,
  PromiseState,
  TIMEOUT,
} from './useRollingApiRequest.props';

// this hook is used to handle multiple promises that are sent to gateway and then polled for a response in case of
// a timeout. The hook will poll for a response on timeout and will stop polling after pollInterval*pollRepeats

/**
/**
 *  @description This hook manages multiple promises sent to a gateway and polls for a response in case of a timeout.
 * It will poll for a response on timeout if the pollingConfig is provided and stop after the allowed run time.
 *
 * The user can specify the number of concurrent requests allowed at any given time, defaulting to 10 if not provided.
 *
 * @param {Array<T>} promisesParams - Array of parameters used to create promises.
 * @param {function(T): Promise<J>} promiseHandler - Function to handle the initial request.
 * @param {PollingConfig<T, J>} [pollingConfig] - Configuration for polling, including poll interval, allowed run time, and poll method. Allowed runt ime in milliseconds.
 * @param {number} [concurrentRequests=10] - Number of concurrent requests allowed at any given time.
 * @param {function(T, number): number} [getRequestId] - Function to get the key for each promise, defaulting to the index of the `promisesParams` array.
 *
 * @returns {MultiPromiseMap<T,J>} result - The result object containing the state of each promise.
 * @returns {function} doRequest - Function to start the API requests.
 * @returns {PromiseState} processStatus - The status of the process.
 */
const useRollingApiRequest = <T, J>({
  promisesParams,
  promiseHandler,
  pollingConfig,
  concurrentRequests = 10,
  getRequestId = (_: T, index: number) => index,
}: LongGatewayRequestParams<T, J>): {
  result: MultiPromiseMap<T, J>;
  doRequest: () => void;
  processStatus: PromiseState;
} => {
  const [multiPromiseOutput, setMultiPromiseOutput] = useState<MultiPromiseMap<T, J>>({});
  const [processStatus, setProcessStatus] = useState<PromiseState>(INITIAL);
  const [pollTime, setPollTime] = useState<number>(0);

  // Component utility functions
  /**
    @description  Function to  creating a promise and setting results appropriately after the promise is resolved
 */
  const handlePromise = useCallback<(params: T, key: number) => Promise<void>>(
    (params: T, key: number) => {
      const promise: Promise<void> = promiseHandler(params)
        .then(response => {
          setMultiPromiseOutput(prevState => ({
            ...prevState,
            [key]: {
              promise,
              state: COMPLETE,
              key,
              result: response,
              promiseParams: params,
            },
          }));
        })
        .catch(error => {
          const statusCode = toString(error.response?.status ?? error.status);
          const needsPolling =
            statusCode === ERROR_408 ||
            statusCode === ERROR_504 ||
            error.code === 'ERR_NETWORK' ||
            error.code === 'ETIMEDOUT';
          if (pollingConfig && needsPolling) {
            const numRetries = 0;
            setMultiPromiseOutput(prevState => ({
              ...prevState,
              [key]: {
                promise,
                state: TIMEOUT,
                key,
                numRetries,
                promiseParams: params,
              },
            }));
          } else if (toString(error.status) === ERROR_409) {
            setMultiPromiseOutput(prevState => ({
              ...prevState,
              [key]: {
                promise,
                state: CONFLICT,
                key,
                promiseParams: params,
              },
            }));
          } else {
            setMultiPromiseOutput(prevState => ({
              ...prevState,
              [key]: {
                promise,
                state: FAILED,
                key,
                promiseParams: params,
              },
            }));
          }
          throw error;
        });

      return promise;
    },
  [promiseHandler, pollingConfig]
  );

  /**
   * @description Function to set a promise as completed and update the result object
   */
  const setPromiseCompleted = useCallback(
    (promiseResult: PromiseResult<T, J>): ((response?: J) => void) =>
      (response?: J) => {
        if (response) {
          setMultiPromiseOutput(prevState => ({
            ...prevState,
            [promiseResult.key]: {
              promise: promiseResult.promise,
              state: COMPLETE,
              key: promiseResult.key,
              result: response,
              promiseParams: promiseResult.promiseParams,
            },
          }));
        }
      },
    []
  );

  /**
   * @description Function that searches the results for objects that have timed out, and then calls the poll method to see if it has completed
   *  and updates the poll time to keep track of elapsed time
   */
  const pollPromises = useCallback(() => {
    if (pollingConfig) {
      return setTimeout(() => {
        Object.values(multiPromiseOutput)
          .filter(promiseResult => promiseResult.state === TIMEOUT)
          .forEach(promiseResult => {
            pollingConfig.pollMethod(promiseResult.promiseParams).then(setPromiseCompleted(promiseResult));
          });
        setPollTime(prevPollTime => prevPollTime + pollingConfig.pollIntervalInMs);
      }, pollingConfig.pollIntervalInMs);
    }
  }, [pollingConfig, multiPromiseOutput, setPromiseCompleted]);

  // 1. doRequest is called to start the process
  const doRequest = useCallback(() => {
    setProcessStatus(PENDING);
  }, []);

  // 2. Initiates the result structure and starts the polling process.
  useEffect(() => {
    if (processStatus === PENDING) {
      const promiseResults = promisesParams.map((params, index) => ({
        promiseParams: params,
        key: getRequestId(params, index),
        state: PENDING,
      }));
      setMultiPromiseOutput(
        promiseResults.reduce(
          (acc, result) => ({
            ...acc,
            [result.key]: result,
          }),
          {}
        )
      );
      setProcessStatus(POLLING);
    }
  }, [promisesParams, handlePromise, processStatus, getRequestId]);

  // 2a. This manages the number of concurrent requests that are allowed
  // to be in play at any given time in a sliding window fashion
  useEffect(() => {
    //   find the total number of pending requests that are currently in play
    const { activeRequests, waitingRequests } = typedObjectValues(multiPromiseOutput).reduce(
      (acc, promiseResult) => {
        // we only look at pending requests because those can either have a promise associated with them or not, but they haven't completed yet
        if (promiseResult.state === PENDING) {
          // if they have a promise and are pending, they are considered active, as the promise will not have resolved yet
          if (promiseResult.promise) {
            acc.activeRequests.push(promiseResult);
          } else {
            // if they don't have a promise, they are waiting to be launched
            acc.waitingRequests.push(promiseResult);
          }
        }
        return acc;
      },
      { activeRequests: [], waitingRequests: [] } as {
        activeRequests: PromiseResult<T, J>[];
        waitingRequests: PromiseResult<T, J>[];
      }
    );
    //   number of allowed concurrent requests - number of active requests
    //   will tell us how many promises to add into our window
    const neededRequestsToAdd = concurrentRequests - activeRequests.length;
    //   for every number in neededRequestsToAdd, find the next waiting request and launch it
    const canAddMoreRequests = neededRequestsToAdd > 0 && waitingRequests.length > 0;
    if (canAddMoreRequests) {
      const updatedPromises = waitingRequests.slice(0, neededRequestsToAdd).map(promiseResult => {
        const newPromise = handlePromise(promiseResult.promiseParams, promiseResult.key);
        return {
          ...promiseResult,
          promise: newPromise,
        };
      });
      // we only update state now if there are additional requests to launch. In this way, we don't trigger
      // an infinite loop of useEffects
      setMultiPromiseOutput(prevState => ({
        ...prevState,
        ...updatedPromises.reduce(
          (acc, promiseResult) => ({
            ...acc,
            [promiseResult.key]: promiseResult,
          }),
          {}
        ),
      }));
    }
  }, [multiPromiseOutput, handlePromise, concurrentRequests]);

  // 3. Manages the polling process. pollPromises is will update the poll time which will trigger this to run again, but only after a timeout
  useEffect(() => {
    // poll time is the amount of time that has elapsed since the request started
    // if the poll time is within the allowed run time, we continue polling
    if (processStatus !== INITIAL && pollingConfig && pollTime < pollingConfig.allowedRunTimeInMs) {
      // pollPromises will update the poll time after the pollInterval has elapsed, which will re-trigger this hook
      const timeoutId = pollPromises();
      // if there is a timeoutId, we return a function that will clear the timeout with garbage collection
      if (timeoutId) {
        return () => clearTimeout(timeoutId);
      }
      //   if the poll time is greater than the allowed run time, we stop polling and mark things as complete
      //   setting the processStatus here will trigger this , but it won't do anything thereafter
    } else if (pollingConfig && pollTime >= pollingConfig.allowedRunTimeInMs) {
      setProcessStatus(COMPLETE);
    }
  }, [pollTime, pollingConfig, pollPromises, processStatus]);

  // 4. Ends the polling process once all promises are complete
  useEffect(() => {
    const resultValues = typedObjectValues(multiPromiseOutput);
    if (
      resultValues.length &&
      resultValues.every(promiseResult => [COMPLETE, FAILED, CONFLICT].includes(promiseResult.state))
    ) {
      setProcessStatus(COMPLETE);
    }
  }, [multiPromiseOutput]);

  useEffect(() => {
    if (processStatus === COMPLETE) {
      setPollTime(0);
    }
  }, [processStatus]);

  return { result: multiPromiseOutput, doRequest, processStatus };
};

export default useRollingApiRequest;
